/**
 * FeatureTable — Free vs Pro comparison matrix (issue #434 AC #4).
 *
 * Renders rows grouped by section header so a procurement officer
 * scanning the matrix doesn't lose their place. Pro cells render
 * a mint checkmark; Free cells render a slate X for boolean
 * values, otherwise the literal value (e.g. "2" / "Unlimited").
 *
 * The matrix is read-only — there's no hover state, no selection.
 * Its only job is to let the reader compare the two columns.
 */
import { Fragment, type ReactElement } from 'react';
import {
  FEATURE_GROUPS,
  type CellValue,
} from '../lib/paywall/planContent';

function renderCell(value: CellValue, column: 'free' | 'pro'): ReactElement {
  if (value === 'check') {
    return (
      <span
        className={
          'feature-cell feature-cell--check' +
          (column === 'pro' ? ' feature-cell--pro' : '')
        }
        aria-label={column === 'pro' ? 'Included in Pro' : 'Included in Free'}
      >
        \u2713
      </span>
    );
  }
  if (value === 'x') {
    return (
      <span
        className="feature-cell feature-cell--x"
        aria-label="Not included"
      >
        \u2715
      </span>
    );
  }
  return <span className="feature-cell-feature">{value}</span>;
}

export function FeatureTable(): ReactElement {
  return (
    <section className="feature-table" aria-label="Free vs Pro comparison">
      <h2 className="onboarding-eyebrow">Compare plans</h2>
      <table className="feature-table-grid">
        <thead>
          <tr>
            <th scope="col">Feature</th>
            <th scope="col">Free</th>
            <th scope="col" className="feature-table-pro-header">
              Pro
            </th>
          </tr>
        </thead>
        <tbody>
          {FEATURE_GROUPS.map((group) => (
            <Fragment key={`group-${group.group}`}>
              <tr className="feature-table-group">
                <th scope="rowgroup" colSpan={3}>
                  {group.group}
                </th>
              </tr>
              {group.rows.map((row) => (
                <tr key={`row-${group.group}-${row.feature}`}>
                  <th scope="row">{row.feature}</th>
                  <td>{renderCell(row.free, 'free')}</td>
                  <td className="feature-table-pro-cell">
                    {renderCell(row.pro, 'pro')}
                  </td>
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </section>
  );
}